/* =============================================================================
   3 GUYS — shared behaviour

   Everything here is bound once, at document level, by delegation. Nothing
   re-binds when the Theme Editor loads, unloads, reorders or duplicates a
   section, so there are no duplicate listeners, stale selectors or global id
   collisions. Sections contribute markup and data attributes, never handlers.
   ============================================================================= */
(function () {
  'use strict';

  var SF = window.SF || {};
  var doc = document;

  /* ---------------------------------------------------------------------------
     Scroll lock.

     `overflow: hidden` on the root, and nothing else. The usual trick —
     position:fixed with a negative top, then scrollTo() on release — collapses
     the document while it is applied, so the restore races the reflow and lands
     somewhere else. Measured: open a lightbox at y=570, close it, arrive at 542;
     do it twice and arrive at 0. `scroll-behavior: smooth` then animates the
     wrong answer, which is how it reads as the page gliding away under you.

     Root overflow keeps the scroll position natively. There is nothing to
     capture and nothing to restore, so there is nothing to get wrong.

     Counted, because more than one overlay can be open in a lifetime and a
     naive lock/unlock pair leaves the page stuck.

     ponytail: iOS below 16 still scrolls the body behind a fixed overlay. The
     panels set overscroll-behavior: contain, which covers the common case; if
     old iOS ever matters, that is where a fixed-position variant would go.
     --------------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------------
     The header's height, published as --header-offset.

     Anything that sticks below the header — the Our Coffee strip, an anchor's
     scroll-margin — needs the header's ACTUAL height, which changes with the
     viewport, with the utility bar being switched off, and the moment someone
     dismisses the announcement. A token cannot know any of that, and the
     approved build's own strip is tucked behind its header because it guesses.
     --------------------------------------------------------------------------- */
  function measureHeader() {
    var header = doc.querySelector('.header-section');
    if (!header) return;
    var h = Math.round(header.getBoundingClientRect().height);
    doc.documentElement.style.setProperty('--header-offset', h + 'px');
  }

  if ('ResizeObserver' in window) {
    var headerEl = doc.querySelector('.header-section');
    if (headerEl) new ResizeObserver(measureHeader).observe(headerEl);
  }
  window.addEventListener('resize', measureHeader);
  doc.addEventListener('DOMContentLoaded', measureHeader);
  measureHeader();

  var lockCount = 0;

  function lockScroll() {
    if (lockCount === 0) {
      /* Hiding the overflow takes the scrollbar away, and the page under the
         overlay jumps 15px wider as it goes. Hold its width back as padding.
         `scrollbar-gutter: stable` cannot do this: the gutter is not reserved
         once the element stops scrolling, and on a short page it would reserve
         room the approved build does not have. */
      var bar = window.innerWidth - doc.documentElement.clientWidth;
      doc.documentElement.style.overflow = 'hidden';
      if (bar > 0) doc.documentElement.style.paddingInlineEnd = bar + 'px';
      doc.body.classList.add('is-locked');
    }
    lockCount++;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      doc.documentElement.style.overflow = '';
      doc.documentElement.style.paddingInlineEnd = '';
      doc.body.classList.remove('is-locked');
    }
  }

  /* ---------------------------------------------------------------------------
     Overlays. One model for the menu, search, cart, lightbox and offer popup:
     open, focus containment, Escape, focus return, scroll restoration.
     --------------------------------------------------------------------------- */
  var openStack = [];

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', 'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function focusable(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null || el === doc.activeElement; }
    );
  }

  function openOverlay(id, trigger) {
    var el = doc.getElementById(id);
    if (!el || openStack.indexOf(el) !== -1) return null;

    el.hidden = false;
    el.__trigger = trigger || doc.activeElement;
    openStack.push(el);
    lockScroll();

    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    /* The base .overlay is visibility:hidden so the transition has somewhere to
       start, and a visibility:hidden element cannot take focus. Both need the
       hidden state to be applied before `is-open` goes on.

       Reading a layout property forces that, synchronously. requestAnimationFrame
       would too, except in a tab the browser has throttled — where the callback
       may not run for minutes. That left the drawer invisible with the page
       already scroll-locked, which is a trap with no way out but a reload. */
    void el.offsetWidth;
    el.classList.add('is-open');
    void el.offsetWidth;

    var first = focusable(el)[0];
    if (first) {
      first.focus();
    } else {
      var panel = el.querySelector('.overlay__panel') || el;
      panel.setAttribute('tabindex', '-1');
      panel.focus();
    }
    el.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));

    return el;
  }

  function closeOverlay(el) {
    var i = openStack.indexOf(el);
    if (i === -1) return;
    openStack.splice(i, 1);

    el.classList.remove('is-open');
    unlockScroll();

    var trigger = el.__trigger;
    if (trigger) {
      if (trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
      if (doc.contains(trigger)) trigger.focus();
    }
    el.__trigger = null;

    var done = function () { if (!el.classList.contains('is-open')) el.hidden = true; };
    var ms = parseFloat(getComputedStyle(doc.documentElement).getPropertyValue('--dur-base')) || 0;
    setTimeout(done, ms + 40);

    el.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
  }

  function closeTop() {
    if (openStack.length) closeOverlay(openStack[openStack.length - 1]);
  }

  doc.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-open-overlay]');
    if (opener) {
      var id = opener.getAttribute('data-open-overlay');
      if (doc.getElementById(id)) {
        e.preventDefault();
        openOverlay(id, opener);
      }
      return;
    }

    if (e.target.closest('[data-close-overlay]')) {
      e.preventDefault();
      closeTop();
      return;
    }

    // A click on the backdrop, outside the panel, closes.
    var overlay = e.target.closest('[data-overlay]');
    if (overlay && !e.target.closest('.overlay__panel')) closeOverlay(overlay);
  });

  doc.addEventListener('keydown', function (e) {
    if (!openStack.length) return;
    var top = openStack[openStack.length - 1];

    if (e.key === 'Escape') {
      e.preventDefault();
      closeTop();
      return;
    }

    if (e.key === 'Tab') {
      var items = focusable(top);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && doc.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && doc.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ---------------------------------------------------------------------------
     Cart.

     Every mutation goes through here so the drawer, the cart page, the card
     quick-add and the product form all share one implementation and one
     authoritative state — the cart object Shopify returns, never a local guess.

     Requests carry a monotonic ticket. A response whose ticket is older than
     the newest issued one is discarded, so rapid repeated clicks can never
     paint a stale total.
     --------------------------------------------------------------------------- */
  var ticket = 0;

  function cartRequest(url, body) {
    var mine = ++ticket;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'cart'), { data: data });
          return data;
        });
      })
      .then(function (data) {
        if (mine !== ticket) return null; // superseded — drop it
        return data;
      });
  }

  function refreshCart() {
    return fetch(SF.routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        publishCart(cart);
        return cart;
      });
  }

  function publishCart(cart) {
    if (!cart) return;
    doc.querySelectorAll('[data-cart-count]').forEach(function (el) {
      var was = parseInt(el.textContent, 10);
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
      /* The bag is often off-screen or behind a drawer when something lands in
         it. One beat on the count is the acknowledgement — it is the only thing
         on the page that moves, and only when the number actually changed. */
      if (was !== cart.item_count && cart.item_count > 0) {
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      }
    });
    paintFreeShipping(cart);
    doc.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  /* Free-delivery progress.
     Off unless the merchant has enabled it AND the store actually carries a
     matching free-shipping rate — this only reports against the configured
     threshold, it does not create a discount. The bar is driven by a custom
     property so it animates a transform rather than its own width. */
  function paintFreeShipping(cart) {
    var rule = SF.freeShipping;
    if (!rule || !rule.enabled || !rule.threshold) return;

    var remaining = Math.max(0, rule.threshold - cart.total_price);
    var progress = Math.min(1, cart.total_price / rule.threshold);

    doc.querySelectorAll('[data-free-shipping-bar]').forEach(function (bar) {
      bar.style.setProperty('--progress', progress.toFixed(4));
    });

    doc.querySelectorAll('[data-free-shipping-note]').forEach(function (note) {
      note.textContent = remaining === 0
        ? (SF.strings.freeUnlocked || '')
        : (SF.strings.freeAway || '').replace('__amt__', formatMoney(remaining));
    });
  }

  /* `AED 204.75`, `AED 42` — the same shape snippets/price.liquid renders, so a
     figure written by script matches the ones rendered by Liquid beside it. */
  function formatMoney(cents) {
    var value = (cents / 100).toLocaleString(SF.lang === 'ar' ? 'ar-AE' : 'en-AE', {
      maximumFractionDigits: 2
    });
    return (SF.currency || 'AED') + ' ' + value;
  }

  function addToCart(payload) {
    return cartRequest(SF.routes.cartAdd, payload).then(function (data) {
      if (!data) return null;
      return refreshCart();
    });
  }

  function changeLine(key, quantity) {
    return cartRequest(SF.routes.cartChange, { id: key, quantity: quantity })
      .then(function (cart) {
        if (!cart) return null;
        publishCart(cart);
        return cart;
      })
      .catch(function (err) {
        /* The number on screen was stepped before the request went out, so the
           control answers the click. If the request then fails — the line is
           gone, the stock ran out, the network dropped — that number is a claim
           about the cart that is not true, and every one of the three callers
           here used to leave it standing. The guard belongs in the shared
           function rather than in each of them: say what happened, then re-read
           the cart so the surface goes back to what Shopify actually holds. */
        announce((err && err.data && err.data.description) || SF.strings.error);
        return refreshCart();
      });
  }

  window.SF = window.SF || {};
  window.SF.cart = { add: addToCart, change: changeLine, refresh: refreshCart };

  /* Add to cart — product form and card quick add share this path. */
  /* ---------------------------------------------------------------------------
     A form's fields, as Shopify's /cart/add.js payload.

     Two shapes, because the theme has two: a product form posts one `id` with
     `properties[...]`, and the bundle posts `items[0][id]`, `items[1][id]` and
     so on. Reading only `id` silently dropped every bundle — the request went
     out with a null id, Shopify refused it, and the button came back looking
     as though it had worked.
     --------------------------------------------------------------------------- */
  function buildAddPayload(fd) {
    var single = fd.get('id');
    if (single) {
      var one = { id: single, quantity: parseInt(fd.get('quantity') || '1', 10) };
      var props = {};
      fd.forEach(function (value, key) {
        var m = key.match(/^properties\[(.+)\]$/);
        if (m && String(value).trim() !== '') props[m[1]] = value;
      });
      if (Object.keys(props).length) one.properties = props;
      return { items: [one] };
    }

    var byIndex = {};
    fd.forEach(function (value, key) {
      var m = key.match(/^items\[(\d+)\]\[(\w+)\](?:\[(.+)\])?$/);
      if (!m) return;
      var item = byIndex[m[1]] || (byIndex[m[1]] = {});
      if (m[2] === 'properties' && m[3]) {
        if (String(value).trim() === '') return;
        item.properties = item.properties || {};
        item.properties[m[3]] = value;
      } else if (m[2] === 'quantity') {
        item.quantity = parseInt(value, 10) || 1;
      } else if (m[2] === 'id') {
        item.id = value;
      }
    });

    var items = Object.keys(byIndex)
      .sort(function (a, b) { return a - b; })
      .map(function (k) { return byIndex[k]; })
      .filter(function (i) { return i.id; });
    items.forEach(function (i) { if (!i.quantity) i.quantity = 1; });
    return { items: items };
  }

  doc.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-cart-add]');
    if (!form) return;
    e.preventDefault();

    var button = form.querySelector('[type="submit"]');
    if (button && button.getAttribute('aria-busy') === 'true') return; // no double submit

    var fd = new FormData(form);
    var payload = buildAddPayload(fd);
    if (!payload.items.length || !payload.items[0].id) {
      announce(SF.strings.error);
      return;
    }

    var label = button ? button.innerHTML : '';
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.innerHTML = SF.strings.adding || 'Adding';
    }

    addToCart(payload)
      .then(function () {
        /* Both arms of the test this replaced called openOverlay with the
           same two arguments: `!drawer.hidden === false` reads as
           `drawer.hidden === true`, and the else did the same thing. */
        if (doc.getElementById('cart-drawer')) openOverlay('cart-drawer', button);
      })
      .catch(function (err) {
        var msg = (err.data && err.data.description) || SF.strings.error;
        var slot = form.querySelector('[data-form-error]');
        if (slot) {
          slot.textContent = msg;
          slot.hidden = false;
        } else {
          announce(msg);
        }
      })
      .finally(function () {
        // The button always comes back, including after a failure.
        if (button) {
          button.removeAttribute('aria-busy');
          button.innerHTML = label;
        }
      });
  });

  /* ---------------------------------------------------------------------------
     Quantity and remove controls inside any cart surface.

     A step is applied to the number on screen straight away, so the control
     answers the click, and the request is held for a moment so a burst of five
     clicks becomes one request for the final quantity rather than five for the
     same one. Shopify's answer is still the authority: the surface re-renders
     from it when it lands.
     --------------------------------------------------------------------------- */
  var pending = {};

  function queueLine(key, qty) {
    var entry = pending[key] || (pending[key] = {});
    entry.qty = qty;
    clearTimeout(entry.timer);
    entry.timer = setTimeout(function () {
      var want = entry.qty;
      delete pending[key];
      changeLine(key, want);
    }, 260);
  }

  doc.addEventListener('click', function (e) {
    var step = e.target.closest('[data-line-step]');
    if (step) {
      e.preventDefault();
      var stepKey = step.getAttribute('data-line-key');
      if (!stepKey) return;
      var row = step.closest('[data-line-item]');
      var out = row && row.querySelector('[data-line-qty]');
      var shown = out ? parseInt(out.textContent, 10) : 1;
      var current = pending[stepKey] ? pending[stepKey].qty : (isNaN(shown) ? 1 : shown);
      var next = Math.max(0, current + (parseInt(step.getAttribute('data-line-step'), 10) || 0));
      if (out) out.textContent = next;
      queueLine(stepKey, next);
      return;
    }

    var btn = e.target.closest('[data-line-change]');
    if (!btn) return;
    e.preventDefault();
    var key = btn.getAttribute('data-line-key');
    var qty = parseInt(btn.getAttribute('data-line-change'), 10);
    if (!key) return;
    btn.setAttribute('aria-busy', 'true');
    changeLine(key, qty).finally(function () { btn.removeAttribute('aria-busy'); });
  });

  doc.addEventListener('change', function (e) {
    var input = e.target.closest('[data-line-input]');
    if (!input) return;
    var key = input.getAttribute('data-line-key');
    var qty = parseInt(input.value, 10);
    if (!key || isNaN(qty) || qty < 0) return;
    changeLine(key, qty);
  });

  /* ---------------------------------------------------------------------------
     Form validation, in the theme's voice and the page's language.

     The browser's own bubble is styled by the operating system and written in
     the BROWSER's language, not the page's — on /ar, with lang="ar" and
     dir="rtl", Chrome still says "Please include an '@' in the email address."
     in a Latin popup pointing the wrong way.

     So the form carries `novalidate`, the constraints are still the browser's
     (`checkValidity`), and the message is drawn under the field in the theme's
     type from a translated string. Turn the script off and the browser's own
     validation comes back — `novalidate` is only ever added from here.
     --------------------------------------------------------------------------- */
  /* Shopify HTML-escapes a translation unless its key ends in _html, so an
     apostrophe arrives as &#39; and textContent would print it literally. */
  var decoder = doc.createElement('textarea');
  function decode(text) {
    if (!text || text.indexOf('&') === -1) return text;
    decoder.innerHTML = text;
    return decoder.value;
  }

  function messageFor(field) {
    var custom = field.getAttribute('data-error');
    if (custom) return custom;
    var strings0 = SF.strings || {};
    var v = field.validity;
    if (v.valueMissing) return decode(strings0.fieldRequired);
    if (v.typeMismatch && field.type === 'email') return decode(strings0.fieldEmail);
    if (v.typeMismatch && field.type === 'tel') return decode(strings0.fieldPhone);
    return decode(strings0.fieldInvalid);
  }

  function errorHost(field) {
    return field.closest('.form-field, .form-row, label') || field.parentElement;
  }

  function clearFieldError(field) {
    field.removeAttribute('aria-invalid');
    var host = errorHost(field);
    var slot = host && host.querySelector('[data-field-error][data-owner="' + field.id + '"]');
    if (slot) slot.remove();
  }

  function showFieldError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    var parent = errorHost(field);
    if (!parent) return;
    var slot = parent.querySelector('[data-field-error][data-owner="' + field.id + '"]');
    if (!slot) {
      slot = doc.createElement('p');
      slot.className = 't-caption field-error';
      slot.setAttribute('data-field-error', '');
      slot.setAttribute('data-owner', field.id || '');
      slot.setAttribute('role', 'alert');
      parent.appendChild(slot);
    }
    slot.textContent = message;
  }

  doc.querySelectorAll('form[data-validate]').forEach(function (form) {
    form.setAttribute('novalidate', '');
    form.addEventListener('submit', function (e) {
      var fields = [].slice.call(form.querySelectorAll('input, select, textarea'));
      var firstBad = null;
      fields.forEach(function (field) {
        if (field.disabled || field.type === 'hidden') return;
        if (field.checkValidity()) { clearFieldError(field); return; }
        showFieldError(field, messageFor(field));
        if (!firstBad) firstBad = field;
      });
      if (firstBad) {
        e.preventDefault();
        firstBad.focus();
      }
    });
    form.addEventListener('input', function (e) {
      var field = e.target;
      if (field.checkValidity && field.checkValidity()) clearFieldError(field);
    });
  });

  /* ---------------------------------------------------------------------------
     A polite live region, for messages with nowhere else to go.
     --------------------------------------------------------------------------- */
  var live;
  function announce(message) {
    if (!live) {
      live = doc.createElement('p');
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      doc.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = message; }, 60);
  }
  window.SF.announce = announce;

  /* ---------------------------------------------------------------------------
     Language switcher — submit on change, with a real button behind noscript.
     --------------------------------------------------------------------------- */
  doc.addEventListener('change', function (e) {
    var select = e.target.closest('[data-localization-select]');
    if (select) select.form.submit();
  });

  /* ---------------------------------------------------------------------------
     Themed select.

     A native <select> opens the operating system's list: grey, system font,
     square corners, nothing to do with the brand — the one thing on the site
     that announces it is a web page. Every `<select data-select-menu>` gets a
     listbox drawn in the theme's own type instead.

     The native control stays as the control of record. It still holds the
     value, it is still what submits, and with no JavaScript it is still what a
     visitor uses — this only ever runs after the list has been built from the
     select's own <option> elements, so the two can never disagree.

     `data-select-menu="up"` drops the list upwards, for a control at the foot
     of a panel.
     --------------------------------------------------------------------------- */
  var TICK = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7"/></svg>';

  function upgradeSelect(select) {
    if (select.dataset.enhanced) return;
    select.dataset.enhanced = '1';

    var wrap = doc.createElement('div');
    wrap.className = 'selectmenu selectmenu--' + (select.dataset.selectMenu === 'up' ? 'up' : 'down');
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);

    var options = [].slice.call(select.options);
    var button = doc.createElement('button');
    button.type = 'button';
    button.className = 'selectmenu__button ' + (select.dataset.selectFace || 't-label');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');

    /* The label is a bare text node on the button rather than a span inside
       it. The build's own market control puts the text directly on the
       inline-flex box, and the mirror pairs the two sides by text — a wrapper
       here reads as a padding and display difference on every sweep. */
    var face = doc.createTextNode('');
    button.appendChild(face);
    button.insertAdjacentHTML('beforeend',
      '<svg class="selectmenu__chevron" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>');

    var list = doc.createElement('div');
    list.className = 'selectmenu__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    var label = select.labels && select.labels[0];
    if (label) list.setAttribute('aria-label', label.textContent.trim());

    var items = options.map(function (option, i) {
      var item = doc.createElement('button');
      item.type = 'button';
      item.className = 'selectmenu__option ' + (select.dataset.selectFace || 't-label');
      item.setAttribute('role', 'option');
      item.dataset.index = i;
      if (option.lang) item.lang = option.lang;
      item.innerHTML = '<span class="selectmenu__tick" aria-hidden="true"></span><span></span>';
      item.lastChild.textContent = option.textContent.trim();
      list.appendChild(item);
      return item;
    });

    function paint() {
      var chosen = options[select.selectedIndex] || options[0];
      face.nodeValue = chosen ? chosen.textContent.trim() : '';
      items.forEach(function (item, i) {
        var on = i === select.selectedIndex;
        item.setAttribute('aria-selected', on ? 'true' : 'false');
        item.firstChild.innerHTML = on ? TICK : '';
      });
    }

    function close() {
      list.hidden = true;
      wrap.classList.remove('is-flipped');
      button.setAttribute('aria-expanded', 'false');
    }

    function open() {
      list.hidden = false;
      button.setAttribute('aria-expanded', 'true');

      /* The list is `max-content`, so it can be wider than the button it hangs
         off. Anchored to the button's end edge it then grows past the start
         edge of the screen — measured at 390: the collection sort opened at
         x = -40 and the drawer's market control at x = -9. Which edge is safe
         depends on where the control happens to sit, which only the open
         control can know, so it checks and flips rather than guessing in CSS.
         Reads once per open, before anything is painted. */
      var box = list.getBoundingClientRect();
      if (box.left < 0 || box.right > doc.documentElement.clientWidth) {
        wrap.classList.add('is-flipped');
      }

      (items[select.selectedIndex] || items[0] || button).focus();
    }

    button.addEventListener('click', function () { if (list.hidden) open(); else close(); });

    items.forEach(function (item, i) {
      item.addEventListener('click', function () {
        if (select.selectedIndex !== i) {
          select.selectedIndex = i;
          /* The native control is what the page listens to, so the change has
             to look like one: the sort form submits on it, and so does the
             locale form. */
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        paint();
        close();
        button.focus();
      });
      item.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus();
      });
    });

    wrap.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !list.hidden) { close(); button.focus(); }
    });
    wrap.addEventListener('focusout', function (e) {
      if (!wrap.contains(e.relatedTarget) && !list.hidden) close();
    });
    doc.addEventListener('click', function (e) {
      if (!wrap.contains(e.target) && !list.hidden) close();
    });
    select.addEventListener('change', paint);

    paint();
    wrap.appendChild(button);
    wrap.appendChild(list);
  }

  function upgradeSelects(root) {
    (root || doc).querySelectorAll('select[data-select-menu]').forEach(upgradeSelect);
  }
  upgradeSelects();
  doc.addEventListener('shopify:section:load', function (e) { upgradeSelects(e.target); });

  /* ---------------------------------------------------------------------------
     Theme Editor lifecycle. Delegated handlers survive on their own; this only
     re-opens an overlay the merchant is actively editing, and closes one whose
     section was removed.
     --------------------------------------------------------------------------- */
  doc.addEventListener('shopify:section:unload', function (e) {
    openStack.slice().forEach(function (el) {
      if (e.target.contains(el)) closeOverlay(el);
    });
  });

  doc.addEventListener('shopify:section:select', function (e) {
    var overlay = e.target.querySelector('[data-overlay]');
    if (overlay && overlay.hidden) openOverlay(overlay.id);
  });

  doc.addEventListener('shopify:section:deselect', function (e) {
    var overlay = e.target.querySelector('[data-overlay]');
    if (overlay && !overlay.hidden) closeOverlay(overlay);
  });

  window.SF.overlay = { open: openOverlay, close: closeOverlay, closeTop: closeTop };

  /* ---------------------------------------------------------------------------
     Product gallery.

     Delegated, so it survives a section reload and works for however many
     galleries a page happens to contain (the PDP has one; a quick-view could
     add another).
     --------------------------------------------------------------------------- */

  function galleryOf(el) { return el.closest('[data-gallery]'); }

  function showMedia(gallery, id, focusThumb) {
    if (!gallery || !id) return;

    gallery.querySelectorAll('[data-gallery-slide]').forEach(function (slide) {
      var match = slide.getAttribute('data-gallery-slide') === String(id);
      slide.hidden = !match;
      // A video that scrolls out of view should not keep playing.
      if (!match) {
        slide.querySelectorAll('video').forEach(function (v) { v.pause(); });
      }
    });

    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      var match = thumb.getAttribute('data-gallery-thumb') === String(id);
      thumb.setAttribute('aria-selected', match ? 'true' : 'false');
      thumb.tabIndex = match ? 0 : -1;
      if (match && focusThumb) thumb.focus();
    });

    gallery.dataset.current = id;
  }

  doc.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-gallery-thumb]');
    if (thumb) {
      showMedia(galleryOf(thumb), thumb.getAttribute('data-gallery-thumb'), false);
      return;
    }

    var opener = e.target.closest('[data-gallery-open]');
    if (opener) {
      e.preventDefault();
      openLightbox(galleryOf(opener), opener.getAttribute('data-gallery-open'), opener);
      return;
    }

    var step = e.target.closest('[data-lightbox-step]');
    if (step) {
      e.preventDefault();
      stepLightbox(parseInt(step.getAttribute('data-lightbox-step'), 10));
    }
  });

  /* Roving focus along the thumbnail rail. */
  doc.addEventListener('keydown', function (e) {
    var thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    var keys = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1, Home: 'first', End: 'last' };
    if (!(e.key in keys)) return;

    var gallery = galleryOf(thumb);
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
    var i = thumbs.indexOf(thumb);
    var move = keys[e.key];
    var next;

    if (move === 'first') next = 0;
    else if (move === 'last') next = thumbs.length - 1;
    else {
      // In RTL the horizontal arrows read the other way round.
      if (SF.rtl && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) move = -move;
      next = (i + move + thumbs.length) % thumbs.length;
    }

    e.preventDefault();
    showMedia(gallery, thumbs[next].getAttribute('data-gallery-thumb'), true);
  });

  var lightboxIds = [];
  var lightboxIndex = 0;

  function paintLightbox() {
    var box = doc.getElementById('gallery-lightbox');
    if (!box) return;
    var id = lightboxIds[lightboxIndex];
    box.querySelectorAll('[data-lightbox-image]').forEach(function (img) {
      img.hidden = img.getAttribute('data-lightbox-image') !== String(id);
    });
    var counter = box.querySelector('[data-lightbox-counter]');
    if (counter) {
      counter.textContent = (SF.strings.imageOf || '__i__ / __n__')
        .replace('__i__', lightboxIndex + 1)
        .replace('__n__', lightboxIds.length);
    }
    var frame = box.querySelector('[data-lightbox-frame]');
    if (frame) frame.scrollTo(0, 0);
  }

  function openLightbox(gallery, id, trigger) {
    var box = doc.getElementById('gallery-lightbox');
    if (!box) return;
    lightboxIds = Array.prototype.map.call(
      box.querySelectorAll('[data-lightbox-image]'),
      function (img) { return img.getAttribute('data-lightbox-image'); }
    );
    lightboxIndex = Math.max(0, lightboxIds.indexOf(String(id)));
    paintLightbox();
    box.addEventListener('overlay:open', function once() {
      box.removeEventListener('overlay:open', once);
      // The scroll frame takes focus so arrow keys pan the enlarged image.
      var frame = box.querySelector('[data-lightbox-frame]');
      if (frame) frame.focus();
    });
    openOverlay('gallery-lightbox', trigger);
  }

  function stepLightbox(delta) {
    if (lightboxIds.length < 2) return;
    lightboxIndex = (lightboxIndex + delta + lightboxIds.length) % lightboxIds.length;
    paintLightbox();
  }

  doc.addEventListener('keydown', function (e) {
    var box = doc.getElementById('gallery-lightbox');
    if (!box || box.hidden) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); stepLightbox(SF.rtl ? -1 : 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); stepLightbox(SF.rtl ? 1 : -1); }
  });

  /* When the lightbox closes, the gallery follows it to whatever was last
     enlarged, so closing does not silently move the reader somewhere else. */
  doc.addEventListener('overlay:close', function (e) {
    if (e.target.id !== 'gallery-lightbox') return;
    var gallery = doc.querySelector('[data-gallery]');
    if (gallery && lightboxIds.length) showMedia(gallery, lightboxIds[lightboxIndex], false);
  });

  /* A variant with its own media selects it in place — no document jump, and
     the quantity and grind choices are untouched. */
  doc.addEventListener('variant:change', function (e) {
    var variant = e.detail && e.detail.variant;
    if (!variant || !variant.featured_media) return;
    var gallery = doc.querySelector('[data-gallery]');
    if (gallery) showMedia(gallery, variant.featured_media.id, false);
  });
  /* Delivery / pickup chips on the product page. Two tabs, one line each. */
  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-fulfil-tab]');
    if (!tab) return;
    var root = tab.closest('[data-fulfil]');
    if (!root) return;
    var want = tab.dataset.fulfilTab;
    root.querySelectorAll('[data-fulfil-tab]').forEach(function (t) {
      var on = t.dataset.fulfilTab === want;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    root.querySelectorAll('[data-fulfil-panel]').forEach(function (p) {
      p.hidden = p.dataset.fulfilPanel !== want;
    });
  });

  /* Shopify's related products arrive from their own endpoint, which renders
     the same section again with `recommendations.performed` true. Swap the
     rendered markup in; leave the placeholder empty if it returns nothing, so
     an empty row collapses rather than showing an empty heading. */
  function loadRecommendations() {
    doc.querySelectorAll('[data-recommendations][data-url]').forEach(function (host) {
      if (host.dataset.loading) return;
      host.dataset.loading = '1';
      fetch(host.dataset.url)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('.product-related-section')
            || parsed.querySelector('[data-recommendations]');
          if (!fresh) return;
          var section = host.closest('.product-related-section') || host;
          section.replaceWith(fresh);
        })
        .catch(function () { /* the row is optional; the page is not */ });
    });
  }

  loadRecommendations();
  doc.addEventListener('shopify:section:load', loadRecommendations);

  /* ---------------------------------------------------------------------------
     Editorial entrance — one observer for the whole document.

     Nothing here hides anything until this code has run: the `.reveal-ready`
     class on <html> is what arms the CSS. So a failed script, a browser with no
     IntersectionObserver, or a reduced-motion preference all leave the page
     fully visible rather than blank.

     Each element reveals once and is then unobserved, so scrolling back to a
     section never hides its content again.
     --------------------------------------------------------------------------- */
  (function () {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });

    /* A group's children come in one after another, capped so a long row never
       trails on for seconds. */
    function ms(value, fallback) {
      var v = String(value || '').trim();
      var n = parseFloat(v);
      if (isNaN(n)) return fallback;
      /* a computed custom property can come back normalised as "0.22s" */
      return /[^m]s$/.test(v) ? n * 1000 : n;
    }

    function stagger(group) {
      var cs = getComputedStyle(doc.documentElement);
      var step = ms(cs.getPropertyValue('--stagger-step'), 50);
      var cap = ms(cs.getPropertyValue('--stagger-max'), 220);
      var kids = group.querySelectorAll(':scope > [data-reveal]');
      for (var i = 0; i < kids.length; i++) {
        kids[i].style.transitionDelay = Math.min(i * step, cap) + 'ms';
      }
    }

    /* Site-wide by request: every content section enters as it arrives, not just
       the editorial ones that were tagged by hand. Done here rather than by
       editing forty section files, so the exclusions stay in one place.

       Header, footer and the overlay group are excluded on purpose — those hold
       the navigation, the drawers and the cart, and hiding them until an
       observer fires would make chrome and commerce depend on this script.

       A specimen sheet is excluded because it brings its own entrance: the rule
       draws, the specimen is laid down, the ticks pin it, the label types and
       the stamp strikes. Tagged here as well it would fade in as one block with
       all of that still hidden inside it. */
    function autoTag(scope) {
      var sections = (scope || doc).querySelectorAll('.shopify-section');
      for (var i = 0; i < sections.length; i++) {
        var s = sections[i];
        /* Place & Visit brings its own choreography too: staggered cards, a
           clip-path wipe per photograph and a day bar that fills. Tagged here
           as well, the gallery would fade in as one block with every wipe
           still hidden inside it. */
        if (/header|footer|overlay|specimen-sheet|shopify-section--place/
            .test(s.className)) continue;
        if (s.hasAttribute('data-reveal')) continue;
        /* Never nest a reveal inside another: the inner one would be held
           hidden by an ancestor that is itself still hidden. */
        if (s.querySelector('[data-reveal]') || s.closest('[data-reveal]')) continue;
        s.setAttribute('data-reveal', '');
      }
    }

    function scan(scope) {
      autoTag(scope);
      (scope || doc).querySelectorAll('[data-reveal-group]').forEach(stagger);
      var els = (scope || doc).querySelectorAll('[data-reveal]:not(.is-revealed)');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        /* Above the fold at first paint: show it outright. The brief is explicit
           that the hero and the primary action must not wait on an entrance. */
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.style.transitionDelay = '0ms';
          el.classList.add('is-revealed');
        } else {
          io.observe(el);
        }
      }
    }

    function init() {
      doc.documentElement.classList.add('reveal-ready');
      scan();
      /* The editor adds, reorders and removes sections; scan only the new one. */
      doc.addEventListener('shopify:section:load', function (e) { scan(e.target); });
    }

    /* Arming and scanning happen in the same frame, after layout: arming early
       would hide everything until a later scan could reveal it. */
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', function () { requestAnimationFrame(init); });
    } else {
      requestAnimationFrame(init);
    }
  })();

})();
